import {
  SanityProductFragment,
  SanityProductGalleryPhoto,
  SanityProductOption,
  SanityProductOptionSetting,
  SanityProductVariantFragment,
} from '@data/sanity/queries/types/product'
import { UrlParameters } from '@lib/hooks'
import { MetadataContext } from '@lib/metadata-context'
import { useActiveVariant } from '@lib/product-hooks'
import { getDefaultVariantId } from '@lib/product-variant'
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react'
import { getCombinedListingProductOptions } from './helpers'
import {
  useActiveCombinedListing,
  useCombinedListingActiveProduct,
} from './hooks'
import { ProductCombinedListing } from './types'

interface ProductCombinedListingContextProps {
  activeCombinedListing?: ProductCombinedListing
  activeProduct?: SanityProductFragment
  activeVariant?: SanityProductVariantFragment
  productGalleryPhotos?: SanityProductGalleryPhoto[]
  productOptionSettings?: SanityProductOptionSetting[]
  productVariants?: SanityProductVariantFragment[]
  productOptions?: SanityProductOption[]
  setActiveVariantId: (variantId: number) => void
}

interface ProductCombinedListingContextProviderProps {
  productSlug?: string
  combinedListing?: ProductCombinedListing
  children: ReactNode
}

export const ProductCombinedListingContext =
  createContext<ProductCombinedListingContextProps>({
    setActiveVariantId: () => null,
  })

export const ProductCombinedListingContextProvider = ({
  productSlug,
  combinedListing: originalCombinedListing,
  children,
}: ProductCombinedListingContextProviderProps) => {
  const { setMetadataOverride } = useContext(MetadataContext)

  const activeCombinedListing = useActiveCombinedListing(
    originalCombinedListing,
  )

  const productVariants = useMemo(() => {
    const productVariants = activeCombinedListing?.products?.flatMap(
      (product) => product.variants ?? [],
    )

    return productVariants
  }, [activeCombinedListing])

  const productOptions = useMemo(() => {
    const productOptions = activeCombinedListing
      ? getCombinedListingProductOptions(activeCombinedListing.products)
      : undefined

    // TODO: With 2 identical option values - duplicate gets removed. Thus option count is lower than needed.
    return productOptions
  }, [activeCombinedListing])

  const productOptionSettings = useMemo(() => {
    const productOptionSettings = activeCombinedListing?.products?.flatMap(
      (product) => {
        return (
          product.optionSettings?.map((optionSetting) => {
            const updatedOptionSetting: SanityProductOptionSetting = {
              ...optionSetting,
            }

            // TODO: Update option key to include product ID (to fix cases when option names are identical between products).
            // if (updatedOptionSetting.forOption) {
            //   const optionKey = getExtendedOptionKey(
            //     updatedOptionSetting.forOption,
            //     product.productID,
            //   )
            //   updatedOptionSetting.forOption = JSON.stringify(optionKey)
            // }

            return updatedOptionSetting
          }) ?? []
        )
      },
    )

    return productOptionSettings
  }, [activeCombinedListing])

  const productGalleryPhotos = useMemo(() => {
    const productGalleryPhotos = activeCombinedListing?.products?.flatMap(
      (product) => {
        return (
          product.galleryPhotos?.map((galleryPhoto) => {
            const updatedGalleryPhoto: SanityProductGalleryPhoto = {
              ...galleryPhoto,
            }

            // TODO: Update option key to include product ID (to fix cases when option names are identical between products).
            // if (updatedGalleryPhoto.forOption) {
            //   const optionKey = getExtendedOptionKey(
            //     updatedGalleryPhoto.forOption,
            //     product.productID,
            //   )
            //   updatedGalleryPhoto.forOption = JSON.stringify(optionKey)
            // }

            return updatedGalleryPhoto
          }) ?? []
        )
      },
    )

    return productGalleryPhotos
  }, [activeCombinedListing])

  const transformUrl = useCallback(
    (url: string, parameters: UrlParameters) => {
      const variantId = parameters?.variant?.[0]
        ? Number(parameters.variant[0])
        : null
      const product = activeCombinedListing?.products?.find((product) => {
        return product.variants?.some((productVariant) => {
          return variantId && productVariant.variantID === variantId
        })
      })

      // Replace URL with product slug
      return product?.slug?.current ?? url
    },
    [activeCombinedListing],
  )

  // Active variant
  const defaultVariantId = useMemo(() => {
    const productBySlug = productSlug
      ? activeCombinedListing?.products?.find(
          (product) => product.slug.current === productSlug,
        )
      : null

    if (productBySlug) {
      return getDefaultVariantId(
        productBySlug.variants ?? [],
        productBySlug.options ?? [],
        productBySlug.optionSettings ?? [],
      )
    }

    return getDefaultVariantId(
      productVariants,
      productOptions,
      productOptionSettings,
    )
  }, [
    activeCombinedListing,
    productOptions,
    productOptionSettings,
    productSlug,
    productVariants,
  ])

  const [activeVariant, setActiveVariantId] = useActiveVariant(
    defaultVariantId,
    productVariants,
    transformUrl,
  )

  // Active product
  const activeProduct = useCombinedListingActiveProduct(
    activeCombinedListing,
    activeVariant,
  )

  // Metadata override
  useEffect(() => {
    setMetadataOverride(activeVariant?.seo ?? activeProduct?.seo ?? null)

    return () => {
      setMetadataOverride(null)
    }
  }, [activeProduct?.seo, activeVariant?.seo, setMetadataOverride])

  return (
    <ProductCombinedListingContext.Provider
      value={{
        activeCombinedListing,
        activeProduct,
        activeVariant,
        productGalleryPhotos,
        productOptionSettings,
        productVariants,
        productOptions,
        setActiveVariantId,
      }}
    >
      {children}
    </ProductCombinedListingContext.Provider>
  )
}
